import { CdkScrollable } from '@angular/cdk/scrolling';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    inject,
    input,
    model,
} from '@angular/core';

import { ModalBase } from '../modal/modal-base';
import { ModalContent } from '../modal/modal-content';
import { ModalCta } from '../modal/modal-cta';
import { ModalTitle } from '../modal/modal-title';

import { UnsavedChangesPromptService } from 'app/services';

export type ModalSize = 'small' | 'medium' | 'large';

@Component({
    selector: 'stby-modal-dialog',
    templateUrl: './modal-dialog.html',
    styleUrl: './modal-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ModalBase, CdkScrollable],
    host: {
        '[class]': "'stby-modal--' + size()",
    },
})
export class ModalDialog {
    private unsavedChangesPromptService = inject(UnsavedChangesPromptService);

    readonly allowWide = input(false, { transform: booleanAttribute });
    readonly allowOverflow = input(false, { transform: booleanAttribute });
    readonly denyClose = input(false, { transform: booleanAttribute });
    readonly closeOnlyByButton = input(false, { transform: booleanAttribute });

    readonly modalOpen = model(false);
    /** Scroll target when content overflows: 'content' scrolls inside the modal (default), 'page' lets the modal grow and scrolls the whole page. */
    readonly scrollBehavior = input<'content' | 'page'>('content');

    readonly size = input<ModalSize>('medium');
    readonly checkHasChangesBeforeClose = input<boolean | (() => boolean)>();
    readonly closeModalPrompt = input<() => void>();

    // denyClose also hides the close button; closeOnlyByButton keeps it but blocks light dismiss.
    readonly preventLightDismiss = computed(() => this.denyClose() || this.closeOnlyByButton());

    private modalTitleQuery = contentChildren(ModalTitle);
    private modalContentQuery = contentChildren(ModalContent);
    private modalCtaQuery = contentChildren(ModalCta);
    modalTitleHasContent = computed(() => this.modalTitleQuery().length > 0);
    modalContentHasContent = computed(() => this.modalContentQuery().length > 0);
    modalCtaHasContent = computed(() => this.modalCtaQuery().length > 0);

    closeModal(): void {
        this.modalOpen.set(false);
    }

    // Passed to ModalBase as `beforeClose`: intercepts every dismiss attempt (backdrop, Escape,
    // close button) and only proceeds if there are no unsaved changes, or the user confirms.
    handleBeforeClose = (close: () => void) => {
        const checkHasChanges = this.checkHasChangesBeforeClose();

        if (checkHasChanges) {
            this.promptClose((yes) => {
                if (yes) close();
            });
        } else {
            close();
        }
    };

    promptClose(callback: (yes: boolean) => void) {
        const closeModalPrompt = this.closeModalPrompt();
        if (closeModalPrompt) {
            closeModalPrompt();
        } else {
            this.unsavedChangesPromptService.generatePrompt('media-modal', () => callback(true));
        }
    }
}
